import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Sparkles, FileText, ScanLine, Languages, Zap, Check, ArrowRight, Star, Shield,
    ArrowUpRight, Lock,
} from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import Seo, { softwareJsonLd } from "@/components/Seo";
import heroKiFoto from "@/assets/hero-ki-foto.webp";
import avatarLisa from "@/assets/avatar-lisa.webp";
import avatarMarkus from "@/assets/avatar-markus.webp";
import avatarAnna from "@/assets/avatar-anna.webp";

export default function HomePage() {
    const { t } = useTranslation();

    return (
        <PublicLayout>
            <Seo
                title="KI-Bewerbung: Anschreiben & Lebenslauf in 60 Sekunden | Bewerbung24.ai"
                description="Erstelle ATS-optimierte Anschreiben und Lebensläufe mit KI — in 9 Sprachen, mit Bewerbungsfoto und ATS-Check. Kostenlos registrieren, erste Bewerbung gratis."
                path="/"
                jsonLd={softwareJsonLd}
            />
            <Hero t={t} />
            <TrustStrip t={t} />
            <ExpertiseUSP />
            <BentoFeatures t={t} />
            <ProcessSection t={t} />
            <Testimonials t={t} />
            <PricingTeaser t={t} />
        </PublicLayout>
    );
}

function Hero({ t }) {
    return (
        <section className="relative overflow-hidden bg-white" data-testid="hero-section">
            <div className="absolute inset-0 pointer-events-none" style={{
                backgroundImage: "radial-gradient(rgba(231,52,63,0.04) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
            }} />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-8 sm:pt-12 lg:pt-28 pb-12 lg:pb-24 grid lg:grid-cols-[1.25fr_1fr] gap-12 lg:gap-16 items-center">
                <div>
                    <div className="h-1.5 w-16 rounded-full mb-4 sm:mb-7" style={{ background: "linear-gradient(35deg, #E7343F 0%, #F9B000 100%)" }} />
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold tracking-[0.18em] uppercase eyebrow-warm bg-red-50 border border-red-100" data-testid="hero-badge">
                        <Sparkles size={12} /> KI-gestützt · DSGVO-konform · Made in Switzerland
                    </span>
                    <h1 className="font-heading mt-5 sm:mt-7 text-4xl sm:text-6xl lg:text-[5.5rem] font-bold tracking-tighter text-warm-strong leading-[0.98] sm:leading-[0.94]">
                        Deine <span className="gradient-warm">KI-Bewerbung</span>.<br />
                        <span className="text-warm">In 60 Sekunden.</span><br />
                        <span>Ohne <span className="gradient-warm">Schreibblockade</span>.</span>
                    </h1>
                    <p className="mt-8 text-lg lg:text-xl text-warm max-w-2xl leading-relaxed">
                        Lade die Stellenanzeige hoch, wir formulieren <strong className="text-warm-strong">Anschreiben und Lebenslauf</strong> exakt
                        passend zur Ausschreibung — in 9 Sprachen, ATS-optimiert, mit professionellem Bewerbungsfoto.
                    </p>
                    <div className="mt-9 flex flex-wrap gap-3" data-testid="hero-cta-row">
                        <Link to="/registrieren" data-testid="hero-register-button" className="group px-7 py-3.5 rounded-full font-semibold btn-primary-grad inline-flex items-center gap-2">
                            Kostenlos starten
                            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                        </Link>
                        <Link to="/preise" data-testid="hero-pricing-link" className="px-7 py-3.5 rounded-full font-semibold text-warm-strong border-2 border-zinc-200 hover:border-warm-red transition-colors inline-flex items-center gap-2">
                            Preise ansehen
                        </Link>
                    </div>
                    <div className="mt-10 flex flex-wrap items-center gap-5 text-sm text-warm">
                        <span className="inline-flex items-center gap-1.5"><Check size={14} className="text-warm-red" /> Keine Kreditkarte</span>
                        <span className="inline-flex items-center gap-1.5"><Check size={14} className="text-warm-red" /> DSGVO · Made in Switzerland</span>
                        <span className="inline-flex items-center gap-1.5"><Check size={14} className="text-warm-red" /> 9 Sprachen</span>
                    </div>
                </div>

                <HeroVisual />
            </div>
        </section>
    );
}

function HeroVisual() {
    return (
        <div className="relative justify-self-center lg:justify-self-end" data-testid="hero-visual">
            <div className="absolute -inset-8 rounded-[3rem] bg-gradient-to-br from-red-100/60 via-amber-100/40 to-amber-100/50 blur-3xl pointer-events-none" />
            <div className="relative w-[340px] sm:w-[420px]">
                {/* Floating photo card */}
                <div className="absolute -left-10 top-8 z-20 w-36 rounded-2xl bg-white shadow-2xl border border-zinc-100 p-3 rotate-[-6deg] animate-float">
                    <div className="aspect-[3/4] rounded-xl bg-gradient-to-br from-zinc-200 to-zinc-100 overflow-hidden flex items-center justify-center">
                        <img src={heroKiFoto} alt="KI-generiertes Bewerbungsfoto" width="300" height="400" className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <p className="mt-2 text-[10px] uppercase tracking-wider text-zinc-500 font-bold">KI-Foto</p>
                </div>
                {/* Main CV preview */}
                <div className="relative rounded-3xl bg-white border border-zinc-200 shadow-2xl p-7" style={{ background: "linear-gradient(180deg, #ffffff 0%, #fafafa 100%)" }}>
                    <div className="flex items-center justify-between mb-4">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Lebenslauf</p>
                        <div className="flex gap-1.5">
                            <span className="h-2 w-2 rounded-full bg-red-300"/><span className="h-2 w-2 rounded-full bg-amber-300"/><span className="h-2 w-2 rounded-full bg-emerald-400"/>
                        </div>
                    </div>
                    <div className="space-y-2.5">
                        <div className="h-3 w-3/4 rounded bg-zinc-900"/>
                        <div className="h-2 w-1/2 rounded bg-zinc-300"/>
                        <div className="mt-4 h-1 w-full rounded bg-zinc-200"/>
                        <div className="mt-3 space-y-1.5">
                            <div className="h-2 w-full rounded bg-zinc-200"/>
                            <div className="h-2 w-11/12 rounded bg-zinc-200"/>
                            <div className="h-2 w-3/4 rounded bg-zinc-200"/>
                        </div>
                        <div className="mt-3 space-y-1.5">
                            <div className="h-2 w-full rounded bg-zinc-200"/>
                            <div className="h-2 w-10/12 rounded bg-zinc-200"/>
                            <div className="h-2 w-4/5 rounded bg-zinc-200"/>
                        </div>
                    </div>
                    {/* ATS badge */}
                    <div className="mt-5 pt-4 border-t border-zinc-100 flex items-center justify-between">
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-200">
                            <Check size={10}/> ATS 94/100
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-zinc-400">Page 1/2</span>
                    </div>
                </div>
                {/* Floating AI chip */}
                <div className="absolute -right-6 -bottom-6 z-20 rounded-2xl bg-zinc-950 text-white p-4 shadow-2xl rotate-[4deg] animate-float" style={{ animationDelay: "0.5s" }}>
                    <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center">
                            <Sparkles size={13} />
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">KI-Score</p>
                            <p className="font-heading text-xl font-bold tracking-tighter">98<span className="text-zinc-500 text-sm">/100</span></p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function TrustStrip() {
    return (
        <div className="border-y border-zinc-100 bg-warm">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                {[
                    { value: "12.000+", label: "Bewerbungen erstellt" },
                    { value: "94%", label: "ATS-konform" },
                    { value: "60 s", label: "Erstellung pro Bewerbung" },
                    { value: "9", label: "Sprachen integriert" },
                ].map((s) => (
                    <div key={s.label} data-testid={`trust-${s.label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                        <p className="font-heading text-4xl font-bold tracking-tighter gradient-warm">{s.value}</p>
                        <p className="mt-1 text-xs uppercase tracking-wider text-warm">{s.label}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ExpertiseUSP() {
    const USPS = [
        "20+ Jahre Personal-, Arbeitsmarkt- und Integrationsexpertise",
        "Professionelle Softwareentwicklung & moderne KI-Technologie",
        "ATS-optimierte Lebensläufe und Anschreiben",
        "KI-gestützte Stärkenanalyse — auch direkt aus dem Lebenslauf",
        "Mehrsprachige Plattform in 9 Sprachen",
        "Für Praktikum, Ausbildung, Studium und Berufseinstieg",
        "Unterstützung von Arbeitsmarktintegration und Fachkräftesicherung",
        "Geeignet für Schulen, Hochschulen, Jobcenter, Bildungsträger und Unternehmen",
    ];
    return (
        <section className="bg-white" data-testid="home-usp-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-16 items-start">
                <div>
                    <p className="eyebrow-warm">Unser USP</p>
                    <h2 className="font-heading mt-4 text-4xl lg:text-5xl font-bold tracking-tighter text-warm-strong leading-[1.05]">
                        KI trifft auf über <span className="gradient-warm">20 Jahre</span> Personal-, Arbeitsmarkt- und Integrationsexpertise.
                    </h2>
                    <p className="mt-6 text-warm leading-relaxed">
                        Bewerbung24.ai ist keine anonyme KI-Spielerei: In die Plattform fließen Erfahrungen aus der
                        Begleitung von Fach- und Führungskräften, beruflicher Neuorientierung, Hochschullehre und
                        Arbeitsmarktintegration ein — damit deine Stärken sichtbar werden.
                    </p>
                    <Link to="/partner" data-testid="home-usp-partner-link" className="mt-7 inline-flex items-center gap-2 font-semibold text-warm-red hover:underline">
                        Mehr über unsere Expertise <ArrowRight size={15} />
                    </Link>
                </div>
                <ul className="grid sm:grid-cols-2 gap-3" data-testid="home-usp-list">
                    {USPS.map((u, i) => (
                        <li key={i} className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-zinc-100 hover:shadow-md transition-shadow">
                            <span className="h-7 w-7 rounded-full flex items-center justify-center text-white shrink-0 mt-0.5" style={{ background: "linear-gradient(35deg, #E7343F 0%, #F9B000 100%)" }}>
                                <Check size={13} strokeWidth={3} />
                            </span>
                            <span className="text-sm font-medium text-warm-strong leading-snug">{u}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
}

function BentoFeatures() {
    return (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28" data-testid="features-section">
            <div className="max-w-2xl">
                <p className="eyebrow-warm">Was du bekommst</p>
                <h2 className="font-heading mt-4 text-4xl lg:text-5xl font-bold tracking-tighter text-warm-strong leading-[1.05]">
                    Sechs Funktionen,<br />
                    <span className="text-warm">die deine Bewerbung</span> <span className="gradient-warm">profilieren</span>.
                </h2>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Big card */}
                <div data-testid="feature-cover" className="md:col-span-2 md:row-span-2 group p-8 lg:p-10 rounded-3xl bg-zinc-950 text-white relative overflow-hidden hover:bg-zinc-900 transition-colors">
                    <div className="absolute -right-20 -bottom-20 h-72 w-72 rounded-full bg-gradient-to-br from-red-500/30 to-amber-500/20 blur-3xl pointer-events-none" />
                    <Sparkles size={28} strokeWidth={1.5} className="text-white" />
                    <h3 className="font-heading mt-7 text-3xl lg:text-4xl font-bold tracking-tight leading-tight">
                        Anschreiben, die <span className="gradient-warm">tatsächlich</span> passen.
                    </h3>
                    <p className="mt-4 text-zinc-400 leading-relaxed max-w-md">
                        Unsere KI liest die Stellenanzeige Wort für Wort. Sie greift Anforderungen,
                        Tonalität und Schlüssel-Skills auf — und liefert ein Anschreiben, das nicht
                        nach KI klingt.
                    </p>
                    <div className="mt-7 flex items-center gap-2 text-sm text-white/80">
                        <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                        <Link to="/funktionen" className="font-semibold">Mehr zur Cover-Letter-KI</Link>
                    </div>
                </div>

                <FeatureCard icon={ScanLine}  title="Lebenslauf-OCR"     text="Foto vom alten CV machen, fertig. Unser OCR-Engine extrahiert Erfahrung, Skills, Daten." />
                <FeatureCard icon={FileText}  title="Smart Templates"    text="Modern, Klassisch, Kreativ. Schriftarten, Farben, Größen anpassbar." />
                <FeatureCard icon={Languages} title="9 Sprachen"         text="Deine Bewerbung auf einen Klick auf Englisch, Türkisch, Arabisch & mehr." />
                <FeatureCard icon={Zap}       title="ATS-Score"          text="Live-Bewertung wie gut deine Bewerbung den Recruiter-Filter passiert." />
                <FeatureCard icon={Shield}    title="DSGVO-konform"      text="Made in Switzerland — Daten verschlüsselt, jederzeit löschbar." />
            </div>
        </section>
    );
}

function FeatureCard({ icon: Icon, title, text }) {
    const id = `feature-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
    return (
        <div data-testid={id} className="group p-6 rounded-3xl bg-white border border-zinc-100 hover:border-warm-red/40 hover:-translate-y-1 transition-all hover:shadow-lg">
            <div className="h-11 w-11 rounded-xl flex items-center justify-center text-white transition-transform group-hover:scale-110" style={{ background: "linear-gradient(35deg, #E7343F 0%, #F9B000 100%)" }}>
                <Icon size={18} strokeWidth={1.5} />
            </div>
            <h3 className="font-heading mt-5 text-lg font-bold text-warm-strong">{title}</h3>
            <p className="mt-1.5 text-sm text-warm leading-relaxed">{text}</p>
        </div>
    );
}

function ProcessSection() {
    return (
        <section className="bg-warm border-y border-zinc-100">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
                <div className="max-w-2xl">
                    <p className="eyebrow-warm">In 3 Schritten</p>
                    <h2 className="font-heading mt-4 text-4xl lg:text-5xl font-bold tracking-tighter text-warm-strong leading-[1.05]">
                        Vom Foto deines alten CVs <span className="gradient-warm">zur fertigen Bewerbung</span>.
                    </h2>
                </div>
                <div className="mt-14 grid md:grid-cols-3 gap-5">
                    {[
                        { step: "01", title: "Profil anlegen", body: "OCR-Upload deines alten Lebenslaufs oder Zeugnisses. KI extrahiert alles automatisch." },
                        { step: "02", title: "Stelle anhängen", body: "Stellenanzeige als Text, PDF oder Screenshot rein. Wir lesen den Job mit." },
                        { step: "03", title: "Download als PDF", body: "Anschreiben + Lebenslauf, fertig formatiert. ATS-Score zeigt, wie stark." },
                    ].map((s, i) => (
                        <div key={s.step} className="p-7 rounded-3xl bg-white hover:shadow-xl transition-shadow" style={{ borderLeft: `5px solid ${i % 2 === 0 ? "#E7343F" : "#F9B000"}` }}>
                            <p className="font-heading text-7xl font-bold tracking-tighter gradient-warm">{s.step}</p>
                            <h3 className="mt-4 font-heading text-xl font-bold text-warm-strong">{s.title}</h3>
                            <p className="mt-2 text-sm text-warm leading-relaxed">{s.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Testimonials() {
    const items = [
        { text: "Ich war so nervös vor meiner Bewerbung als Mechatronikerin — die KI hat den Ton perfekt getroffen. Wurde direkt zum Gespräch eingeladen.", author: "Lisa M.", role: "Auszubildende, 19", avatar: avatarLisa },
        { text: "Nach 20 Jahren im selben Beruf wollte ich wechseln. Bewerbung24 hat aus meinem alten Lebenslauf eine moderne Mappe gemacht.", author: "Markus T.", role: "Quereinsteiger, 42", avatar: avatarMarkus },
        { text: "Als Studentin im 6. Semester habe ich keine Zeit für Bewerbungen. 60 Sekunden — und die Bewerbung ist raus.", author: "Anna S.", role: "Studentin, BWL", avatar: avatarAnna },
    ];
    return (
        <section className="bg-zinc-950 text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
                backgroundImage: "radial-gradient(rgba(249,176,0,0.6) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
            }} />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
                <div className="max-w-2xl">
                    <p className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: "#F9B000" }}>Bewerber sagen</p>
                    <h2 className="font-heading mt-4 text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.05]">
                        Echte Bewerbungen. <span className="gradient-warm">Echte Erfolge.</span>
                    </h2>
                </div>
                <div className="mt-14 grid md:grid-cols-3 gap-5">
                    {items.map((t, i) => (
                        <figure key={t.author} data-testid={`testimonial-${i}`} className="p-7 rounded-3xl bg-white/[0.04] border border-white/10 backdrop-blur">
                            <div className="flex gap-0.5 mb-5 text-amber-400">
                                {Array.from({ length: 5 }).map((_, k) => <Star key={k} size={14} className="fill-amber-400" />)}
                            </div>
                            <blockquote className="text-base lg:text-lg text-white leading-relaxed">&bdquo;{t.text}&ldquo;</blockquote>
                            <figcaption className="mt-6 pt-5 border-t border-white/10 flex items-center gap-3">
                                <img src={t.avatar} alt={t.author} width="40" height="40" className="h-10 w-10 rounded-full object-cover" loading="lazy" />
                                <div>
                                    <p className="font-semibold text-sm">{t.author}</p>
                                    <p className="text-xs text-zinc-400">{t.role}</p>
                                </div>
                            </figcaption>
                        </figure>
                    ))}
                </div>
            </div>
        </section>
    );
}

function PricingTeaser() {
    return (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
            <div className="max-w-2xl">
                <p className="eyebrow-warm">Pricing</p>
                <h2 className="font-heading mt-4 text-4xl lg:text-5xl font-bold tracking-tighter text-warm-strong leading-[1.05]">
                    Eine <span className="gradient-warm">Bewerbung</span> kostet weniger als ein Mittagessen.
                </h2>
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/preise" data-testid="pricing-teaser-link" className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold btn-primary-grad">
                    Pakete ansehen <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </Link>
                <Link to="/partner" data-testid="pricing-teaser-partner" className="px-7 py-3.5 rounded-full font-semibold text-warm-strong border-2 border-zinc-200 hover:border-warm-red transition-colors inline-flex items-center gap-2">
                    <Lock size={14} /> Für Partner & Institutionen
                </Link>
            </div>
        </section>
    );
}
